import { useState, useEffect, useRef } from 'react';
import { useJobForm } from '../../contexts/JobFormContext';
import { JobImageUpload } from './JobImageUpload';
import { RichTextEditor } from '../ui/RichTextEditor';
import { JobPreview } from './JobPreview';
import { AIJobDescriptionGenerator } from './AIJobDescriptionGenerator';
import { AIFieldGenerator } from './AIFieldGenerator';
import { AIGenerateAllButton } from './AIGenerateAllButton';
import { EducationalQualificationsSection } from './EducationalQualificationsSection';
import { useCompanies, useCreateCompany, useCompanyAssets } from '../../hooks/unified/useCompaniesQueries';
import { useJobComposerTemplates } from '../../hooks/unified/useJobTemplatesQueries';
import { useViewport } from '../../hooks/useViewport';
import { useToast } from '../../contexts/ToastContext';
import { env } from '../../config/env';
import { unifiedApiService } from '../../services/unified';
import type { Job, JobTemplate, AITemplateContext } from '../../services/unified/types';
import { isAITemplate } from '../../services/unified/types';

interface JobComposerProps {
  jobId?: string;
  onSave?: (job: Job | null) => void;
  onPublish?: (job: Job | null) => void;
  onCancel?: () => void;
}

export function JobComposer({ jobId }: JobComposerProps) {
  // Get form context
  const {
    formData,
    updateField,
  } = useJobForm();

  const { isMobile } = useViewport();
  const [activeTab, setActiveTab] = useState<'form' | 'preview'>('form');
  const [requirementInput, setRequirementInput] = useState('');
  const [benefitInput, setBenefitInput] = useState('');
  const [companySearch, setCompanySearch] = useState('');
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  
  // AI Template state
  const [selectedAITemplate, setSelectedAITemplate] = useState<JobTemplate | null>(null);
  const [aiTemplateContext, setAITemplateContext] = useState<AITemplateContext | null>(null);
  const [isGeneratingFromTemplate, setIsGeneratingFromTemplate] = useState(false);
  
  const { showSuccess, showError } = useToast();
  const { data: companies = [] } = useCompanies();
  const { data: companyAssets = [] } = useCompanyAssets(formData.company_id || '');
  const { data: composerTemplates } = useJobComposerTemplates(formData.company_id);
  const createCompanyMutation = useCreateCompany();
  
  // Use ref to track if we've already auto-populated the logo
  const logoAutoPopulatedRef = useRef(false);

  // Helper function to get full image URL
  const getImageUrl = (imageUrl: string | null | undefined): string | null => {
    if (!imageUrl) return null;
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return imageUrl;
    }
    return `${env.API_BASE_URL}${imageUrl}`;
  };

  // Filter companies based on search
  const filteredCompanies = companies.filter(company =>
    company.name.toLowerCase().includes(companySearch.toLowerCase())
  );

  // Auto-populate logo when company is selected
  useEffect(() => {
    // Only run if we have a company_id, no logo yet, and haven't auto-populated before
    if (formData.company_id && !formData.company_logo_url && !logoAutoPopulatedRef.current && companies.length > 0) {
      const selectedCompany = companies.find(c => c.id === formData.company_id);
      
      // Try to get logo from company default_logo_url first
      if (selectedCompany?.default_logo_url) {
        updateField('company_logo_url', selectedCompany.default_logo_url);
        logoAutoPopulatedRef.current = true;
      } 
      // If no default logo, try to get from company assets
      else if (companyAssets.length > 0) {
        const defaultLogo = companyAssets.find(asset => asset.asset_type === 'logo' && asset.is_default);
        const anyLogo = companyAssets.find(asset => asset.asset_type === 'logo');
        const logoToUse = defaultLogo || anyLogo;
        
        if (logoToUse) {
          updateField('company_logo_url', logoToUse.url);
          logoAutoPopulatedRef.current = true;
        }
      }
    }
    
    // Reset the ref when company changes
    if (!formData.company_id) {
      logoAutoPopulatedRef.current = false;
    }
  }, [formData.company_id, companies, companyAssets, formData.company_logo_url, updateField]);

  // Auto-populate job image from company assets
  useEffect(() => {
    if (formData.company_id && companyAssets.length > 0) {
      const defaultImage = companyAssets.find(asset => asset.asset_type === 'image' && asset.is_default);
      const anyImage = companyAssets.find(asset => asset.asset_type === 'image');
      const imageToUse = defaultImage || anyImage;
      
      // Only set if no image is currently set
      if (imageToUse && !formData.job_image_url) {
        updateField('job_image_url', imageToUse.url);
      }
    }
  }, [formData.company_id, companyAssets]);

  // Handle company selection
  const handleCompanySelect = (companyId: string, companyName: string, logoUrl?: string) => {
    
    const selectedCompany = companies.find(c => c.id === companyId);
    
    updateField('company_id', companyId);
    updateField('company_name', companyName);
    
    // Auto-fill logo - use passed logoUrl or company's default_logo_url
    const companyLogoUrl = logoUrl || selectedCompany?.default_logo_url;
    
    if (companyLogoUrl) {
      updateField('company_logo_url', companyLogoUrl);
    }
    
    // Auto-fill location from headquarters if available
    if (selectedCompany?.headquarters) {
      try {
        const hq = typeof selectedCompany.headquarters === 'string' 
          ? JSON.parse(selectedCompany.headquarters)
          : selectedCompany.headquarters;
        
        if (hq.city && hq.country) {
          const location = [hq.city, hq.state, hq.country].filter(Boolean).join(', ');
          if (!formData.location) {
            updateField('location', location);
          }
        }
      } catch (e) {
        // Ignore parsing errors
      }
    }
    
    // Auto-fill benefits from company if available
    if (selectedCompany?.benefits && (!formData.benefits || formData.benefits.length === 0)) {
      try {
        const benefits = typeof selectedCompany.benefits === 'string'
          ? JSON.parse(selectedCompany.benefits)
          : selectedCompany.benefits;
        
        if (Array.isArray(benefits) && benefits.length > 0) {
          updateField('benefits', benefits);
        }
      } catch (e) {
        // Ignore parsing errors
      }
    }
    
    setCompanySearch(companyName);
    setShowCompanyDropdown(false);
    
    showSuccess('Company selected', `${companyName} information has been auto-filled`);
  };

  // Handle logo selection (from asset library only)
  const handleLogoUploaded = async (url: string) => {
    updateField('company_logo_url', url);
    
    // If company name is entered but no company_id, create the company
    if (formData.company_name && !formData.company_id) {
      try {
        const newCompany = await createCompanyMutation.mutateAsync({
          name: formData.company_name,
          default_logo_url: url,
        });
        updateField('company_id', newCompany.id);
        showSuccess('Company created', `${newCompany.name} has been created`);
      } catch (error) {
        console.error('Failed to create company:', error);
      }
    }
  };

  // Handle job image selection (from asset library only)
  const handleJobImageUploaded = async (url: string) => {
    updateField('job_image_url', url);
  };





  // Handle requirements
  const addRequirement = () => {
    if (requirementInput.trim()) {
      updateField('requirements', [...formData.requirements, requirementInput.trim()]);
      setRequirementInput('');
    }
  };

  const removeRequirement = (index: number) => {
    updateField('requirements', formData.requirements.filter((_, i) => i !== index));
  };

  // Handle benefits
  const addBenefit = () => {
    if (benefitInput.trim()) {
      updateField('benefits', [...formData.benefits, benefitInput.trim()]);
      setBenefitInput('');
    }
  };

  const removeBenefit = (index: number) => {
    updateField('benefits', formData.benefits.filter((_, i) => i !== index));
  };

  // Handle template selection
  const handleTemplateSelect = (templateId: string) => {
    if (!templateId) {
      // Clear template if empty selection
      updateField('template_id', undefined);
      setSelectedAITemplate(null);
      setAITemplateContext(null);
      return;
    }

    // Get all templates from composer templates (both regular and AI)
    const allTemplates = [
      ...(composerTemplates?.regularTemplates || []),
      ...(composerTemplates?.aiTemplates || [])
    ];
    
    const template = allTemplates.find((t: JobTemplate) => t.id === templateId);
    if (!template) return;

    // Set the template ID
    updateField('template_id', templateId);

    // Check if this is an AI template (Requirements 2.1, 2.4)
    if (isAITemplate(template)) {
      // For AI templates: show context preview, don't populate fields
      setSelectedAITemplate(template);
      setAITemplateContext(template.ai_context || null);
      showSuccess('AI Template selected', `Click "Generate with AI" to create content from "${template.name}"`);
      return;
    }

    // For regular templates: populate fields immediately (existing behavior)
    setSelectedAITemplate(null);
    setAITemplateContext(null);

    try {
      const jobData = typeof template.job_data === 'string' 
        ? JSON.parse(template.job_data)
        : template.job_data;

      // Populate all fields from template
      if (jobData.title) updateField('title', jobData.title);
      if (jobData.description) updateField('description', jobData.description);
      if (jobData.location) updateField('location', jobData.location);
      if (jobData.salary_min) updateField('salary_min', jobData.salary_min);
      if (jobData.salary_max) updateField('salary_max', jobData.salary_max);
      if (jobData.job_type) updateField('job_type', jobData.job_type);
      if (jobData.experience_level) updateField('experience_level', jobData.experience_level);
      if (jobData.requirements && Array.isArray(jobData.requirements)) {
        updateField('requirements', jobData.requirements);
      }
      if (jobData.benefits && Array.isArray(jobData.benefits)) {
        updateField('benefits', jobData.benefits);
      }
      if (jobData.educational_qualifications && Array.isArray(jobData.educational_qualifications)) {
        updateField('educational_qualifications', jobData.educational_qualifications);
      }

      // Also populate logo and images from company if not already set
      if (formData.company_id) {
        const selectedCompany = companies.find(c => c.id === formData.company_id);
        
        // Populate logo if not set
        if (selectedCompany?.default_logo_url && !formData.company_logo_url) {
          updateField('company_logo_url', selectedCompany.default_logo_url);
        }
        
        // Populate job image from assets if not set
        if (companyAssets.length > 0 && !formData.job_image_url) {
          const defaultImage = companyAssets.find(asset => asset.asset_type === 'image' && asset.is_default);
          const anyImage = companyAssets.find(asset => asset.asset_type === 'image');
          const imageToUse = defaultImage || anyImage;
          
          if (imageToUse) {
            updateField('job_image_url', imageToUse.url);
          }
        }
      }

      showSuccess('Template applied', `Job fields have been populated from "${template.name}"`);
    } catch (error) {
      console.error('Failed to parse template data:', error);
      showError('Error', 'Failed to apply template');
    }
  };

  // Handle AI template generation (Requirements 2.2, 2.3, 2.5)
  const handleGenerateFromAITemplate = async () => {
    if (!selectedAITemplate || !formData.company_id) {
      showError('Error', 'Please select a company and AI template first');
      return;
    }

    setIsGeneratingFromTemplate(true);

    try {
      const result = await unifiedApiService.ai.generateFromTemplate(
        selectedAITemplate.id,
        formData.company_id,
        formData.title || undefined
      );

      // Populate fields with generated content
      if (result.title) updateField('title', result.title);
      if (result.description) updateField('description', result.description);
      if (result.requirements && result.requirements.length > 0) {
        updateField('requirements', result.requirements);
      }
      if (result.benefits && result.benefits.length > 0) {
        updateField('benefits', result.benefits);
      }
      if (result.educational_qualifications && result.educational_qualifications.length > 0) {
        updateField('educational_qualifications', result.educational_qualifications);
      }

      // Clear AI template selection after successful generation
      setSelectedAITemplate(null);
      setAITemplateContext(null);

      showSuccess('AI Generated', 'Job content has been generated from the template!');
    } catch (error) {
      console.error('Failed to generate from AI template:', error);
      showError('Generation Failed', error instanceof Error ? error.message : 'Failed to generate content');
    } finally {
      setIsGeneratingFromTemplate(false);
    }
  };

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Mobile Tab Navigation */}
      {isMobile && (
        <div className="bg-white border-b border-gray-200 px-4 py-2 flex gap-2">
          <button
            onClick={() => setActiveTab('form')}
            className={`flex-1 py-2.5 px-4 text-sm font-medium rounded-lg min-h-[44px] ${
              activeTab === 'form'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Edit
          </button>
          <button
            onClick={() => setActiveTab('preview')}
            className={`flex-1 py-2.5 px-4 text-sm font-medium rounded-lg min-h-[44px] ${
              activeTab === 'preview'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Preview
          </button>
        </div>
      )}

      {/* Split Screen Layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left: Form */}
        <div className={`${isMobile ? (activeTab === 'form' ? 'w-full' : 'hidden') : 'w-1/2'} overflow-y-auto p-4 sm:p-6 space-y-4 sm:space-y-6`}>
          {/* Company Selection */}
          <div className="bg-white rounded-lg shadow p-4 sm:p-6 space-y-4">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900">Company Information</h2>

            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Company *
              </label>
              <input
                type="text"
                value={companySearch || formData.company_name || ''}
                onChange={(e) => {
                  setCompanySearch(e.target.value);
                  updateField('company_name', e.target.value);
                  setShowCompanyDropdown(true);
                  // Clear company_id if user is typing a new name
                  if (formData.company_id) {
                    updateField('company_id', undefined);
                  }
                }}
                onFocus={() => setShowCompanyDropdown(true)}
                onBlur={() => setTimeout(() => setShowCompanyDropdown(false), 200)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[44px]"
                placeholder="e.g., TechCorp Inc. (type to search or create new)"
              />
              
              {/* Company Dropdown */}
              {showCompanyDropdown && filteredCompanies.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                  {filteredCompanies.map((company) => (
                    <button
                      key={company.id}
                      type="button"
                      onClick={() => handleCompanySelect(company.id, company.name, company.default_logo_url)}
                      className="w-full px-3 py-2 text-left hover:bg-gray-100 flex items-center gap-2"
                    >
                      {company.default_logo_url && getImageUrl(company.default_logo_url) && (
                        <img
                          src={getImageUrl(company.default_logo_url)!}
                          alt={company.name}
                          className="w-6 h-6 rounded object-cover"
                        />
                      )}
                      <span>{company.name}</span>
                    </button>
                  ))}
                </div>
              )}
              
              {formData.company_id && (
                <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Existing company selected
                </p>
              )}
              {formData.company_name && !formData.company_id && (
                <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  New company - will be created when you upload a logo
                </p>
              )}
            </div>

            {/* Company Logo Preview and Upload */}
            {formData.company_logo_url && formData.company_logo_url.trim() !== '' ? (
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <img
                    src={getImageUrl(formData.company_logo_url)!}
                    alt="Company logo"
                    className="w-16 h-16 object-contain rounded"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">Company Logo</p>
                    <p className="text-xs text-gray-500">Logo will appear on job listing</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      updateField('company_logo_url', '');
                    }}
                    className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-md border border-red-200"
                  >
                    Remove
                  </button>
                </div>
                {formData.company_id && (
                  <button
                    type="button"
                    onClick={() => {
                      // Trigger the asset library or file upload
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = 'image/*';
                      input.onchange = async (e) => {
                        const file = (e.target as HTMLInputElement).files?.[0];
                        if (file) {
                          // Handle file upload here
                          // For now, just show a message
                        }
                      };
                      input.click();
                    }}
                    className="w-full px-3 py-2 text-sm text-primary-600 hover:bg-primary-50 rounded-md border border-primary-200"
                  >
                    Change Logo
                  </button>
                )}
              </div>
            ) : (
              <div>
                <JobImageUpload
                  type="logo"
                  currentImageUrl={formData.company_logo_url}
                  onImageUploaded={handleLogoUploaded}
                  onImageDeleted={() => updateField('company_logo_url', undefined)}
                  showAssetLibrary={true}
                  companyId={formData.company_id}
                  hideLabel={false}
                />
              </div>
            )}
          </div>

          {/* Template Selection - Only show if company is selected (Requirements 3.1, 3.2, 3.3) */}
          {formData.company_id ? (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="border-l-4 border-primary-500 bg-primary-50 p-4 rounded-r-lg">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-primary-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-900 mb-1">
                      Use Template (Optional)
                    </label>
                    <p className="text-xs text-gray-600 mb-2">
                      {selectedAITemplate 
                        ? 'AI Template selected - click "Generate with AI" to create content'
                        : formData.template_id 
                          ? 'Template applied - you can change or clear it below'
                          : 'Start with a pre-configured template to speed up job creation'
                      }
                    </p>
                    
                    {/* Check if company has any templates */}
                    {(!composerTemplates?.regularTemplates?.length && !composerTemplates?.aiTemplates?.length) ? (
                      <div className="text-sm text-gray-500 italic py-2">
                        No templates available for this company. Create templates in the Template Management section.
                      </div>
                    ) : (
                      <select
                        value={formData.template_id || ''}
                        onChange={(e) => handleTemplateSelect(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white text-sm"
                      >
                        <option value="">Select a template...</option>
                        
                        {/* AI Templates group (Requirements 3.4) */}
                        {composerTemplates?.aiTemplates && composerTemplates.aiTemplates.length > 0 && (
                          <optgroup label="🤖 AI Templates">
                            {composerTemplates.aiTemplates.map((template: JobTemplate) => (
                              <option key={template.id} value={template.id}>
                                {template.name}
                              </option>
                            ))}
                          </optgroup>
                        )}
                        
                        {/* Regular Templates group */}
                        {composerTemplates?.regularTemplates && composerTemplates.regularTemplates.length > 0 && (
                          <optgroup label="📋 Templates">
                            {composerTemplates.regularTemplates.map((template: JobTemplate) => (
                              <option key={template.id} value={template.id}>
                                {template.name}
                              </option>
                            ))}
                          </optgroup>
                        )}
                      </select>
                    )}
                  </div>
                </div>
              </div>
              
              {/* AI Template Context Preview (Requirements 2.1) */}
              {selectedAITemplate && aiTemplateContext && (
                <div className="mt-4 border-l-4 border-purple-500 bg-purple-50 p-4 rounded-r-lg">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-purple-900 flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                        </svg>
                        AI Template: {selectedAITemplate.name}
                      </h4>
                      <p className="text-xs text-purple-700 mt-1 mb-3">
                        This template will use AI to generate content based on the following context:
                      </p>
                      
                      {/* Context Preview */}
                      <div className="space-y-2 text-xs">
                        {aiTemplateContext.title_context && (
                          <div className="bg-white/50 p-2 rounded">
                            <span className="font-medium text-purple-800">Title:</span>
                            <span className="text-purple-700 ml-1">{aiTemplateContext.title_context}</span>
                          </div>
                        )}
                        {aiTemplateContext.description_context && (
                          <div className="bg-white/50 p-2 rounded">
                            <span className="font-medium text-purple-800">Description:</span>
                            <span className="text-purple-700 ml-1">{aiTemplateContext.description_context.substring(0, 100)}...</span>
                          </div>
                        )}
                        {aiTemplateContext.requirements_context && (
                          <div className="bg-white/50 p-2 rounded">
                            <span className="font-medium text-purple-800">Requirements:</span>
                            <span className="text-purple-700 ml-1">{aiTemplateContext.requirements_context.substring(0, 100)}...</span>
                          </div>
                        )}
                        {aiTemplateContext.benefits_context && (
                          <div className="bg-white/50 p-2 rounded">
                            <span className="font-medium text-purple-800">Benefits:</span>
                            <span className="text-purple-700 ml-1">{aiTemplateContext.benefits_context.substring(0, 100)}...</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Generate Button (Requirements 2.2, 2.5) */}
                    <button
                      type="button"
                      onClick={handleGenerateFromAITemplate}
                      disabled={isGeneratingFromTemplate}
                      className="px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap"
                    >
                      {isGeneratingFromTemplate ? (
                        <>
                          <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Generating...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                          Generate with AI
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* No company selected message (Requirements 3.2) */
            <div className="bg-white rounded-lg shadow p-6">
              <div className="border-l-4 border-gray-300 bg-gray-50 p-4 rounded-r-lg">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-sm text-gray-600">
                      💡 <span className="font-medium">Tip:</span> Select a company first to see available job templates
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* AI Generate All Section */}
          {formData.company_id && formData.title && (
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg shadow p-6 border border-purple-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-purple-900 flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                    AI-Powered Content Generation
                  </h3>
                  <p className="text-xs text-purple-700 mt-1">
                    Generate description, requirements, and benefits with one click
                  </p>
                </div>
                <AIGenerateAllButton
                  jobTitle={formData.title}
                  experienceLevel={formData.experience_level}
                  location={formData.location}
                  companyContext={{
                    id: formData.company_id,
                    name: formData.company_name,
                    industry: companies.find(c => c.id === formData.company_id)?.industry,
                    description: companies.find(c => c.id === formData.company_id)?.description,
                  }}
                  onGenerated={(content) => {
                    if (content.description) {
                      updateField('description', content.description);
                    }
                    if (content.requirements && content.requirements.length > 0) {
                      updateField('requirements', content.requirements);
                    }
                    if (content.benefits && content.benefits.length > 0) {
                      updateField('benefits', content.benefits);
                    }
                    showSuccess('AI Generated', 'Job content has been generated successfully!');
                  }}
                  disabled={!formData.title}
                />
              </div>
            </div>
          )}

          {/* Basic Info */}
          <div className="bg-white rounded-lg shadow p-4 sm:p-6 space-y-4">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900">Job Details</h2>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Job Title *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => updateField('title', e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[44px]"
                placeholder="e.g., Senior Software Engineer"
              />
              {formData.validation_errors.title && (
                <p className="text-sm text-red-600 mt-1">{formData.validation_errors.title}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location *
              </label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => updateField('location', e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[44px]"
                placeholder="e.g., San Francisco, CA"
              />
              {formData.validation_errors.location && (
                <p className="text-sm text-red-600 mt-1">{formData.validation_errors.location}</p>
              )}
            </div>
          </div>

          {/* Job Image */}
          <div className="bg-white rounded-lg shadow p-4 sm:p-6 space-y-4">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900">Job Image</h2>
            <p className="text-sm text-gray-500">
              Add a featured image for this job posting (optional)
            </p>

            {/* Job Image Preview and Upload */}
            {formData.job_image_url && formData.job_image_url.trim() !== '' ? (
              <div className="space-y-3">
                <div className="relative rounded-lg overflow-hidden border border-gray-200">
                  <img
                    src={getImageUrl(formData.job_image_url)!}
                    alt="Job image"
                    className="w-full h-48 object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
                    <p className="text-xs text-white font-medium">Featured Image</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      updateField('job_image_url', '');
                    }}
                    className="flex-1 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md border border-red-200"
                  >
                    Remove Image
                  </button>
                  {formData.company_id && (
                    <button
                      type="button"
                      onClick={() => {
                        // Trigger the asset library or file upload
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.accept = 'image/*';
                        input.onchange = async (e) => {
                          const file = (e.target as HTMLInputElement).files?.[0];
                          if (file) {
                            // Handle file upload
                          }
                        };
                        input.click();
                      }}
                      className="flex-1 px-3 py-2 text-sm text-primary-600 hover:bg-primary-50 rounded-md border border-primary-200"
                    >
                      Change Image
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div>
                <JobImageUpload
                  type="job-image"
                  currentImageUrl={formData.job_image_url}
                  onImageUploaded={handleJobImageUploaded}
                  onImageDeleted={() => updateField('job_image_url', undefined)}
                  showAssetLibrary={true}
                  companyId={formData.company_id}
                  hideLabel={false}
                />
              </div>
            )}
          </div>

          {/* Description */}
          <div className="bg-white rounded-lg shadow p-4 sm:p-6 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900">Job Description</h2>
              <AIJobDescriptionGenerator
                jobTitle={formData.title}
                experienceLevel={formData.experience_level || 'mid'}
                keySkills={formData.requirements}
                onContentGenerated={(content) => {
                  // Only update description if it was generated
                  if (content.description) {
                    updateField('description', content.description);
                  }
                }}
              />
            </div>
            <RichTextEditor
              value={formData.description || ''}
              onChange={(value) => updateField('description', value)}
              placeholder="Enter job description..."
              minHeight="300px"
            />
            {formData.validation_errors.description && (
              <p className="text-sm text-red-600 mt-1">{formData.validation_errors.description}</p>
            )}
          </div>

          {/* Job Details */}
          <div className="bg-white rounded-lg shadow p-4 sm:p-6 space-y-4">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900">Job Details</h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Salary Min (PA)
                </label>
                <input
                  type="number"
                  inputMode="numeric"
                  value={formData.salary_min || ''}
                  onChange={(e) => updateField('salary_min', e.target.value ? parseInt(e.target.value) : undefined)}
                  step={10000}
                  min={0}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[44px]"
                  placeholder="e.g., 300000"
                />
                {formData.validation_errors.salary_min && (
                  <p className="text-sm text-red-600 mt-1">{formData.validation_errors.salary_min}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Salary Max (PA)
                </label>
                <input
                  type="number"
                  inputMode="numeric"
                  value={formData.salary_max || ''}
                  onChange={(e) => updateField('salary_max', e.target.value ? parseInt(e.target.value) : undefined)}
                  step={10000}
                  min={0}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[44px]"
                  placeholder="e.g., 500000"
                />
                {formData.validation_errors.salary_max && (
                  <p className="text-sm text-red-600 mt-1">{formData.validation_errors.salary_max}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Job Type
                </label>
                <select
                  value={formData.job_type || 'full-time'}
                  onChange={(e) => updateField('job_type', e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[44px]"
                >
                  <option value="full-time">Full-time</option>
                  <option value="part-time">Part-time</option>
                  <option value="contract">Contract</option>
                  <option value="internship">Internship</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Experience Level
                </label>
                <select
                  value={formData.experience_level || 'mid'}
                  onChange={(e) => updateField('experience_level', e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[44px]"
                >
                  <option value="entry">Entry Level</option>
                  <option value="mid">Mid Level</option>
                  <option value="senior">Senior Level</option>
                  <option value="lead">Lead</option>
                </select>
              </div>
            </div>
          </div>

          {/* Requirements */}
          <div className="bg-white rounded-lg shadow p-4 sm:p-6 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900">Requirements *</h2>
              <AIFieldGenerator
                fieldType="requirements"
                jobTitle={formData.title}
                companyName={formData.company_name}
                experienceLevel={formData.experience_level}
                onGenerated={(reqs) => {
                  if (Array.isArray(reqs)) {
                    updateField('requirements', reqs);
                    showSuccess('AI Generated', 'Requirements have been generated');
                  }
                }}
                disabled={!formData.title}
              />
            </div>

            <div className="flex gap-2">
              <input
                type="text"
                value={requirementInput}
                onChange={(e) => setRequirementInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addRequirement())}
                className="flex-1 px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[44px]"
                placeholder="e.g., React, TypeScript, 5+ years experience"
              />
              <button
                type="button"
                onClick={addRequirement}
                className="px-4 py-2.5 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 min-h-[44px]"
              >
                Add
              </button>
            </div>

            <div className="space-y-2">
              {formData.requirements.map((req, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                  <span className="text-sm text-gray-700 flex-1 mr-2">{req}</span>
                  <button
                    type="button"
                    onClick={() => removeRequirement(index)}
                    className="text-red-600 hover:text-red-700 p-1 min-w-[44px] min-h-[44px] flex items-center justify-center"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
            {formData.validation_errors.requirements && (
              <p className="text-sm text-red-600 mt-1">{formData.validation_errors.requirements}</p>
            )}
          </div>

          {/* Benefits */}
          <div className="bg-white rounded-lg shadow p-4 sm:p-6 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900">Benefits</h2>
              <AIFieldGenerator
                fieldType="benefits"
                jobTitle={formData.title}
                companyId={formData.company_id}
                companyName={formData.company_name}
                companyInfo={companies.find(c => c.id === formData.company_id)?.description}
                experienceLevel={formData.experience_level}
                onGenerated={(benefits) => {
                  if (Array.isArray(benefits)) {
                    updateField('benefits', benefits);
                    showSuccess('AI Generated', 'Benefits have been generated');
                  }
                }}
                disabled={!formData.title}
              />
            </div>

            <div className="flex gap-2">
              <input
                type="text"
                value={benefitInput}
                onChange={(e) => setBenefitInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addBenefit())}
                className="flex-1 px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[44px]"
                placeholder="e.g., Health Insurance, Remote Work, 401k"
              />
              <button
                type="button"
                onClick={addBenefit}
                className="px-4 py-2.5 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 min-h-[44px]"
              >
                Add
              </button>
            </div>

            <div className="space-y-2">
              {formData.benefits.map((benefit, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                  <span className="text-sm text-gray-700 flex-1 mr-2">{benefit}</span>
                  <button
                    type="button"
                    onClick={() => removeBenefit(index)}
                    className="text-red-600 hover:text-red-700 p-1 min-w-[44px] min-h-[44px] flex items-center justify-center"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Educational Qualifications */}
          <div className="bg-white rounded-lg shadow p-4 sm:p-6 space-y-4">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900">Educational Qualifications</h2>
            <p className="text-sm text-gray-500">
              Specify educational requirements for this position (optional)
            </p>
            <EducationalQualificationsSection
              qualifications={formData.educational_qualifications}
              onChange={(qualifications) => updateField('educational_qualifications', qualifications)}
              error={formData.validation_errors.educational_qualifications}
            />
          </div>

          {/* Featured Status */}
          <div className="bg-white rounded-lg shadow p-4 sm:p-6 space-y-4">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900">Settings</h2>

            <div>
              <label className="flex items-center space-x-3 min-h-[44px]">
                <input
                  type="checkbox"
                  checked={formData.is_featured}
                  onChange={(e) => updateField('is_featured', e.target.checked)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 w-5 h-5"
                />
                <span className="text-sm font-medium text-gray-700">Featured Job</span>
              </label>
              <p className="mt-1 text-sm text-gray-500">
                Featured jobs appear prominently on the home page
              </p>
            </div>
          </div>

          {/* Completeness Indicator */}
          <div className="bg-white rounded-lg shadow p-4 sm:p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Completeness</span>
              <span className="text-sm font-semibold text-gray-900">{formData.completeness_percentage}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${formData.completeness_percentage}%` }}
              />
            </div>
            {formData.completeness_percentage < 100 && (
              <p className="mt-2 text-xs text-gray-500">
                Complete all required fields to publish this job
              </p>
            )}
          </div>
        </div>

        {/* Right: Preview */}
        <div className={`${isMobile ? (activeTab === 'preview' ? 'w-full' : 'hidden') : 'w-1/2 border-l border-gray-200'} overflow-y-auto bg-gray-100`}>
          <JobPreview 
            job={{
              id: jobId || '',
              title: formData.title,
              company: formData.company_name,
              location: formData.location,
              description: formData.description,
              company_logo_url: formData.company_logo_url,
              job_image_url: formData.job_image_url,
              salary_min: formData.salary_min,
              salary_max: formData.salary_max,
              job_type: formData.job_type,
              experience_level: formData.experience_level,
              requirements: formData.requirements,
              benefits: formData.benefits,
              status: formData.status as any,
              created_at: new Date().toISOString(),
            }} 
            isPreviewMode={true} 
          />
        </div>
      </div>

    </div>
  );
}
